import { useCallback, useEffect, useRef } from 'react'

type DebouncedFunction<T extends (...args: never[]) => void> = {
  (...args: Parameters<T>): void
  /** Cancel any pending debounced call without executing it */
  cancel: () => void
  /** Execute any pending debounced call immediately */
  flush: () => void
}

type DebouncedCallbackOptions = {
  flushOnUnmount?: boolean
}

/**
 * Returns a debounced version of the callback that delays invocation
 * until after `delayMs` milliseconds have elapsed since the last call.
 * Flushes pending calls on unmount by default to prevent data loss.
 * Set `flushOnUnmount: false` to cancel pending calls on unmount instead.
 */
export function useDebouncedCallback<T extends (...args: never[]) => void>(
  callback: T,
  delayMs: number,
  { flushOnUnmount = true }: DebouncedCallbackOptions = {},
): DebouncedFunction<T> {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const callbackRef = useRef(callback)
  const pendingArgsRef = useRef<Parameters<T> | null>(null)
  const flushOnUnmountRef = useRef(flushOnUnmount)

  useEffect(() => {
    flushOnUnmountRef.current = flushOnUnmount
  }, [flushOnUnmount])

  // Keep callback ref up to date
  useEffect(() => {
    callbackRef.current = callback
  }, [callback])

  const cancel = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
    pendingArgsRef.current = null
  }, [])

  const flush = useCallback(() => {
    if (timeoutRef.current && pendingArgsRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
      const args = pendingArgsRef.current
      pendingArgsRef.current = null
      callbackRef.current(...args)
    }
  }, [])

  const debouncedFn = useCallback(
    (...args: Parameters<T>) => {
      cancel()
      pendingArgsRef.current = args
      timeoutRef.current = setTimeout(() => {
        pendingArgsRef.current = null
        callbackRef.current(...args)
      }, delayMs)
    },
    [delayMs, cancel],
  ) as DebouncedFunction<T>

  debouncedFn.cancel = cancel
  debouncedFn.flush = flush

  // Flush pending calls on unmount by default to prevent data loss
  useEffect(() => {
    return () => {
      if (timeoutRef.current && pendingArgsRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
        const args = pendingArgsRef.current
        pendingArgsRef.current = null
        if (flushOnUnmountRef.current) callbackRef.current(...args)
      }
    }
  }, [])

  return debouncedFn
}
